import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Tabs, Tab, Form, Modal } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaEdit, FaQrcode } from 'react-icons/fa';
import axios from 'axios';
import QRCode from 'qrcode';

const GymMemberDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [payments, setPayments] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [showMeasurementModal, setShowMeasurementModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [trainers, setTrainers] = useState([]);
  const [editData, setEditData] = useState({
    name: '', phone: '', email: '', dateOfBirth: '', gender: 'male',
    address: '', emergencyContact: '', bloodGroup: '', trainerId: ''
  });
  const [measurementData, setMeasurementData] = useState({
    weight: '', height: '', chest: '', waist: '', arms: '', thighs: ''
  });

  useEffect(() => {
    fetchMemberDetails();
    fetchTrainers();
  }, [id]);

  const fetchTrainers = async () => {
    try {
      const res = await axios.get('/api/gym/trainers');
      setTrainers(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchMemberDetails = async () => {
    try {
      const [memberRes, membershipRes, paymentRes, attendanceRes, measurementRes] = await Promise.all([
        axios.get(`/api/gym/members/${id}`),
        axios.get('/api/gym/memberships', { params: { memberId: id } }),
        axios.get('/api/gym/payments', { params: { memberId: id } }),
        axios.get('/api/gym/attendance', { params: { memberId: id } }),
        axios.get('/api/gym/measurements', { params: { memberId: id } })
      ]);

      setMember(memberRes.data);
      setMemberships(Array.isArray(membershipRes.data) ? membershipRes.data : []);
      setPayments(Array.isArray(paymentRes.data) ? paymentRes.data : []);
      setAttendance(Array.isArray(attendanceRes.data) ? attendanceRes.data : []);
      setMeasurements(Array.isArray(measurementRes.data) ? measurementRes.data : []);
    } catch (err) {
      console.error(err);
    }
  };

  const generateQR = async () => {
    try {
      const qr = await QRCode.toDataURL(`GYM-MEMBER-${member.memberId}`);
      setQrCode(qr);
      setShowQR(true);
    } catch (err) {
      console.error(err);
    }
  };

  const handleEditClick = () => {
    setEditData({
      name: member.name,
      phone: member.phone,
      email: member.email || '',
      dateOfBirth: member.dateOfBirth || '',
      gender: member.gender || 'male',
      address: member.address || '',
      emergencyContact: member.emergencyContact || '',
      bloodGroup: member.bloodGroup || '',
      trainerId: member.trainerId || ''
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`/api/gym/members/${id}`, { ...editData, id });
      alert('Member updated successfully!');
      setShowEditModal(false);
      fetchMemberDetails();
    } catch (err) {
      console.error('Error updating member:', err);
      alert('Error updating member: ' + (err.response?.data?.error || err.message));
    }
  };

  const saveMeasurement = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post('/api/gym/measurements', { memberId: id, ...measurementData });
      if (response.data.success) {
        alert('Measurement saved successfully!');
        setShowMeasurementModal(false);
        setMeasurementData({ weight: '', height: '', chest: '', waist: '', arms: '', thighs: '' });
        fetchMemberDetails();
      } else {
        alert('Failed to save measurement: ' + (response.data.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error saving measurement:', err);
      alert('Error saving measurement: ' + (err.response?.data?.error || err.message));
    }
  };

  if (!member) return (
    <Container fluid className="py-4">
      <div className="text-center py-5">
        <div className="spinner-border text-success" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading member details...</p>
      </div>
    </Container>
  );

  // Check if member data is valid
  if (!member.name || !member.memberId) {
    return (
      <Container fluid className="py-4">
        <Button variant="outline-secondary" className="mb-3" onClick={() => navigate('/gym/members')}>
          <FaArrowLeft className="me-2" /> Back
        </Button>
        <div className="text-center py-5">
          <h5 className="text-danger">Member not found</h5>
          <p>The member you're looking for doesn't exist.</p>
          <Button variant="primary" onClick={() => navigate('/gym/members')}>Go to Members</Button>
        </div>
      </Container>
    );
  }

  const bmi = measurements[0] ? (measurements[0].weight / ((measurements[0].height / 100) ** 2)).toFixed(1) : 'N/A';

  return (
    <Container fluid className="py-4">
      <Button variant="outline-secondary" className="mb-3" onClick={() => navigate('/gym/members')}>
        <FaArrowLeft className="me-2" /> Back
      </Button>

      <Row className="mb-4">
        <Col md={4}>
          <Card className="shadow-sm">
            <Card.Body className="text-center">
              <div className="mb-3" style={{ width: '120px', height: '120px', margin: '0 auto', borderRadius: '50%', background: '#28a745', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '48px', fontWeight: 'bold' }}>
                {member?.name?.charAt(0)?.toUpperCase() || 'M'}
              </div>
              <h4>{member?.name || 'Unknown'}</h4>
              <p className="text-muted">{member?.memberId || 'N/A'}</p>
              <Badge bg={member.status === 'active' ? 'success' : 'danger'}>{member.status?.toUpperCase()}</Badge>
              <hr />
              <p><strong>Phone:</strong> {member.phone}</p>
              <p><strong>Email:</strong> {member.email || 'N/A'}</p>
              <p><strong>Gender:</strong> {member.gender}</p>
              <p><strong>Blood Group:</strong> {member.bloodGroup || 'N/A'}</p>
              <p><strong>Join Date:</strong> {new Date(member.joinDate).toLocaleDateString()}</p>
              <Button variant="primary" className="me-2" onClick={handleEditClick}><FaEdit /> Edit</Button>
              <Button variant="info" onClick={generateQR}><FaQrcode /> QR Code</Button>
            </Card.Body>
          </Card>
        </Col>

        <Col md={8}>
          <Tabs defaultActiveKey="memberships" className="mb-3">
            <Tab eventKey="memberships" title="Memberships">
              <Card>
                <Card.Body>
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>Package</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                        <th>Amount</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {memberships.map(m => (
                        <tr key={m.id}>
                          <td>{m.package?.name}</td>
                          <td>{new Date(m.startDate).toLocaleDateString()}</td>
                          <td>{new Date(m.endDate).toLocaleDateString()}</td>
                          <td>Rs. {m.finalAmount}</td>
                          <td><Badge bg={m.status === 'active' ? 'success' : 'danger'}>{m.status}</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Tab>

            <Tab eventKey="payments" title="Payments">
              <Card>
                <Card.Body>
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Method</th>
                        <th>Package</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map(p => (
                        <tr key={p.id}>
                          <td>{new Date(p.paymentDate).toLocaleDateString()}</td>
                          <td>Rs. {p.amount}</td>
                          <td><Badge bg="info">{p.paymentMethod}</Badge></td>
                          <td>{p.package?.name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Tab>

            <Tab eventKey="attendance" title="Attendance">
              <Card>
                <Card.Body>
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Check In</th>
                        <th>Check Out</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendance.slice(0, 30).map(a => {
                        const formatTime = (time) => {
                          if (!time) return '-';
                          const [h, m] = time.split(':');
                          const hour = parseInt(h);
                          const ampm = hour >= 12 ? 'PM' : 'AM';
                          const hour12 = hour % 12 || 12;
                          return `${hour12}:${m} ${ampm}`;
                        };
                        return (
                          <tr key={a.id}>
                            <td>{new Date(a.date).toLocaleDateString()}</td>
                            <td>{formatTime(a.checkIn)}</td>
                            <td>{formatTime(a.checkOut)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Tab>

            <Tab eventKey="measurements" title="Body Measurements">
              <Card>
                <Card.Body>
                  <Button variant="success" className="mb-3" onClick={() => setShowMeasurementModal(true)}>Add Measurement</Button>
                  {measurements[0] && (
                    <Row className="mb-3">
                      <Col md={6}>
                        <Card className="text-center">
                          <Card.Body>
                            <h3>{measurements[0].weight} kg</h3>
                            <p className="text-muted">Weight</p>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col md={6}>
                        <Card className="text-center">
                          <Card.Body>
                            <h3>{bmi}</h3>
                            <p className="text-muted">BMI</p>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  )}
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Weight</th>
                        <th>Height</th>
                        <th>Chest</th>
                        <th>Waist</th>
                        <th>Arms</th>
                      </tr>
                    </thead>
                    <tbody>
                      {measurements.map(m => (
                        <tr key={m.id}>
                          <td>{new Date(m.createdAt).toLocaleDateString()}</td>
                          <td>{m.weight} kg</td>
                          <td>{m.height} cm</td>
                          <td>{m.chest} cm</td>
                          <td>{m.waist} cm</td>
                          <td>{m.arms} cm</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Tab>
          </Tabs>
        </Col>
      </Row>

      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Member</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleEditSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control required value={editData.name} onChange={(e) => setEditData({ ...editData, name: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone *</Form.Label>
                  <Form.Control required value={editData.phone} onChange={(e) => setEditData({ ...editData, phone: e.target.value })} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control type="email" value={editData.email} onChange={(e) => setEditData({ ...editData, email: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Date of Birth</Form.Label>
                  <Form.Control type="date" value={editData.dateOfBirth} onChange={(e) => setEditData({ ...editData, dateOfBirth: e.target.value })} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Gender</Form.Label>
                  <Form.Select value={editData.gender} onChange={(e) => setEditData({ ...editData, gender: e.target.value })}>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Blood Group</Form.Label>
                  <Form.Control value={editData.bloodGroup} onChange={(e) => setEditData({ ...editData, bloodGroup: e.target.value })} />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control as="textarea" rows={2} value={editData.address} onChange={(e) => setEditData({ ...editData, address: e.target.value })} />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Emergency Contact</Form.Label>
                  <Form.Control value={editData.emergencyContact} onChange={(e) => setEditData({ ...editData, emergencyContact: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Assign Trainer</Form.Label>
                  <Form.Select value={editData.trainerId} onChange={(e) => setEditData({ ...editData, trainerId: e.target.value })}>
                    <option value="">No Trainer</option>
                    {trainers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Button type="submit" variant="success">Update Member</Button>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal show={showMeasurementModal} onHide={() => setShowMeasurementModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Body Measurement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={saveMeasurement}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Weight (kg)</Form.Label>
                  <Form.Control type="number" step="0.1" required value={measurementData.weight} onChange={(e) => setMeasurementData({ ...measurementData, weight: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Height (cm)</Form.Label>
                  <Form.Control type="number" required value={measurementData.height} onChange={(e) => setMeasurementData({ ...measurementData, height: e.target.value })} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Chest (cm)</Form.Label>
                  <Form.Control type="number" value={measurementData.chest} onChange={(e) => setMeasurementData({ ...measurementData, chest: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Waist (cm)</Form.Label>
                  <Form.Control type="number" value={measurementData.waist} onChange={(e) => setMeasurementData({ ...measurementData, waist: e.target.value })} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Arms (cm)</Form.Label>
                  <Form.Control type="number" value={measurementData.arms} onChange={(e) => setMeasurementData({ ...measurementData, arms: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Thighs (cm)</Form.Label>
                  <Form.Control type="number" value={measurementData.thighs} onChange={(e) => setMeasurementData({ ...measurementData, thighs: e.target.value })} />
                </Form.Group>
              </Col>
            </Row>
            <Button type="submit" variant="success">Save Measurement</Button>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal show={showQR} onHide={() => setShowQR(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Member QR Code</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <img src={qrCode} alt="QR Code" style={{ width: '300px' }} />
          <p className="mt-3"><strong>{member.name}</strong></p>
          <p>{member.memberId}</p>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default GymMemberDetail;
